/**
 * @file Pure transpose of a table slice's Typesafe column judgments into a
 * per-source-column score lookup the review UI ranks against. The model scores
 * the column axis target-first (each `ColumnPlan` is one target field's winning
 * source column plus its runner-up source columns; `unmappedTargets` holds the
 * target fields with no winning column), but every ColumnCard and the unmapped /
 * add-mapping pickers rank source-column-first. This flips that: for every
 * `(sourceColumn, targetField)` pair the model judged it records the probability
 * — the plan's `confidence` for the winner, the `alternatives` probabilities for
 * runners-up, and the `unmappedTargets` probabilities for unwon targets. Built
 * from the slice's untouched base axes (`baseColumns`, `unmappedTargets`), never
 * the live editable clones, and memoized per slice by `MappingReviewColumns`.
 */
import type { Snapshot } from 'valtio';
import type { ColumnAlternative, ColumnPlan } from '#types';

export function mappingReviewColumnScores(
  baseColumns: readonly Snapshot<ColumnPlan>[],
  unmappedTargets: Snapshot<Record<string, ColumnAlternative[]>>,
): Map<string, Map<string, number>> {
  const bySource = new Map<string, Map<string, number>>();
  const record = (sourceColumn: string, targetField: string, probability: number): void => {
    const row = bySource.get(sourceColumn) ?? new Map<string, number>();
    row.set(targetField, probability);
    bySource.set(sourceColumn, row);
  };

  for (const plan of baseColumns) {
    record(plan.sourceColumn, plan.targetField, plan.confidence);
    for (const alternative of plan.alternatives) {
      record(alternative.sourceColumn, plan.targetField, alternative.probability);
    }
  }
  for (const [targetField, alternatives] of Object.entries(unmappedTargets)) {
    for (const alternative of alternatives) {
      record(alternative.sourceColumn, targetField, alternative.probability);
    }
  }

  return bySource;
}
